import React from 'react';
import { cn } from '../../lib/utils';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';
import { MultiSelectFilter } from './MultiSelectFilter';
import { TabbedSections } from './TabbedSections';
import { vatOptions } from './TopSection';
import { DropdownSelector } from './DropdownSelector';
import { PriceAlertChangeType } from '../../stores/appFiltersStore';

interface FilterModalProps {
  // Order filters - Restaurant filters (multi-select, for "All Restaurants" view)
  restaurantFilters?: string[];
  setRestaurantFilters?: (filters: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];

  // Order filters - Platform filters (multi-select)
  platformFilters?: string[];
  setPlatformFilters?: (filters: string[]) => void;
  availablePlatforms?: string[];

  // Order filters - Section filters (multi-select for restaurant sections)
  sectionFilters?: string[];
  setSectionFilters?: (filters: string[]) => void;
  availableSections?: { id: string; name: string }[];

  // Order filters - Category filters (multi-select for specific categories)
  categoryFilters?: string[];
  setCategoryFilters?: (filters: string[]) => void;
  availableCategories?: string[];

  // Order filters - Subcategory filters (multi-select for specific subcategories)
  subCategoryFilters?: string[];
  setSubCategoryFilters?: (filters: string[]) => void;
  availableSubCategories?: string[];

  // Revenue filters - Restaurant filters (multi-select, for "All Restaurants" view)
  revenueRestaurantFilters?: string[];
  setRevenueRestaurantFilters?: (filters: string[]) => void;
  availableRevenueRestaurants?: { id: string; name: string }[];

  // Revenue filters - Revenue source / venue filters (multi-select, by source NAME)
  revenueSourceFilters?: string[];
  setRevenueSourceFilters?: (filters: string[]) => void;
  availableRevenueSources?: string[];

  // Revenue filters - Revenue category filters (multi-select)
  revenueCategoryFilters?: string[];
  setRevenueCategoryFilters?: (filters: string[]) => void;
  availableRevenueCategories?: string[];

  // Revenue filters - Menu category filters (multi-select)
  menuCategoryFilters?: string[];
  setMenuCategoryFilters?: (filters: string[]) => void;
  availableMenuCategories?: { id: string; name: string }[];

  // Price alert filters - Restaurant filters (multi-select, for "All Restaurants" view)
  priceAlertRestaurantFilters?: string[];
  setPriceAlertRestaurantFilters?: (filters: string[]) => void;
  availablePriceAlertRestaurants?: { id: string; name: string }[];

  // Price alert filters - Platform filters (multi-select)
  priceAlertPlatformFilters?: string[];
  setPriceAlertPlatformFilters?: (filters: string[]) => void;
  availablePriceAlertPlatforms?: string[];

  // Price alert filters - Section filters (multi-select) - shares sectionFilters with order history
  priceAlertSectionFilters?: string[];
  setPriceAlertSectionFilters?: (filters: string[]) => void;
  availablePriceAlertSections?: { id: string; name: string }[];

  // Price alert filters - Category filters (multi-select)
  priceAlertCategoryFilters?: string[];
  setPriceAlertCategoryFilters?: (filters: string[]) => void;
  availablePriceAlertCategories?: string[];

  // Price alert filters - Change type (all/increases/decreases)
  priceAlertChangeType?: PriceAlertChangeType;
  setPriceAlertChangeType?: (type: PriceAlertChangeType) => void;

  // VAT toggle (only shown on mobile when filters are active)
  onVatChange?: (value: string) => void;
  currentVatValue?: string;
  showVatToggle?: boolean;

  onClose: () => void;
  onReset: () => void;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

export const FilterModal: React.FC<FilterModalProps> = ({
  restaurantFilters = [],
  setRestaurantFilters,
  availableRestaurants = [],
  platformFilters = [],
  setPlatformFilters,
  availablePlatforms = [],
  sectionFilters = [],
  setSectionFilters,
  availableSections = [],
  categoryFilters = [],
  setCategoryFilters,
  availableCategories = [],
  subCategoryFilters = [],
  setSubCategoryFilters,
  availableSubCategories = [],
  revenueRestaurantFilters = [],
  setRevenueRestaurantFilters,
  availableRevenueRestaurants = [],
  revenueSourceFilters = [],
  setRevenueSourceFilters,
  availableRevenueSources = [],
  revenueCategoryFilters = [],
  setRevenueCategoryFilters,
  availableRevenueCategories = [],
  menuCategoryFilters = [],
  setMenuCategoryFilters,
  availableMenuCategories = [],
  priceAlertRestaurantFilters = [],
  setPriceAlertRestaurantFilters,
  availablePriceAlertRestaurants = [],
  priceAlertPlatformFilters = [],
  setPriceAlertPlatformFilters,
  availablePriceAlertPlatforms = [],
  priceAlertSectionFilters = [],
  setPriceAlertSectionFilters,
  availablePriceAlertSections = [],
  priceAlertCategoryFilters = [],
  setPriceAlertCategoryFilters,
  availablePriceAlertCategories = [],
  priceAlertChangeType,
  setPriceAlertChangeType,
  onVatChange,
  currentVatValue,
  showVatToggle = false,
  onClose,
  onReset,
  className,
  align = 'right',
}) => {
  const { t } = useTranslation();
  // Capitalize first letter of platform
  const formatPlatformName = (platform: string) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // Format platform options for display
  const formatPlatformOptions = (platforms: string[]) => {
    return platforms.map(p => ({ id: p, name: formatPlatformName(p) }));
  };

  // Inside FilterModal component – replace the entire dropdownContainer logic
  const dropdownContainer = cn(
    // Base styles - always full-width on mobile, max-width on larger
    // `bg-white` alone kept this panel white in dark mode while every child switched to
    // dark tokens — unreadable. Light literal kept as-is; only the dark pair is added.
    'absolute top-full mt-2 w-[95vw] max-w-[330px] z-50 bg-white dark:bg-card-dark shadow-xl rounded-xl border',
    borderPrimary,
  
    // Right-aligned to the anchor at every width. The old <550px "center on the
    // button" override assumed a full-width Filters button; the button is now a
    // compact chip at the right edge of the chips row, so centering on it would
    // push the panel off-screen. Right-aligned it opens inward and stays visible.
    'right-0',
    'max-[550px]:w-[92vw]',
  
    // Optional: extra safety for very small screens
    'max-[400px]:w-[96vw]'
  );
  


  return (
    <div className={cn(dropdownContainer, className)}>
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 relative">
          <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>{t('common.filters')}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Sections - Max height 450px with scroll */}
        <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto pr-2">
          {/* Restaurant Filters (Multi-select, shown first when All Restaurants is selected) */}
          {availableRestaurants.length > 0 && setRestaurantFilters && (
            <MultiSelectFilter
              label="Restaurants"
              selectedValues={restaurantFilters}
              onValuesChange={setRestaurantFilters}
              availableOptions={availableRestaurants}
              valueType="default"
              maxHeight="max-h-[90px]"
            />
          )}

          {/* VAT Toggle (only shown on mobile when filters are active) */}
          {showVatToggle && onVatChange && (
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                VAT
              </label>
              <DropdownSelector
                options={vatOptions}
                defaultValue={currentVatValue || vatOptions[0].value}
                onChange={onVatChange}
                alwaysFullWidth={true}
              />
            </div>
          )}

          {/* Section Filters (Multi-select for Ordering Accounts) */}
          {availableSections.length > 0 && setSectionFilters && (
            <MultiSelectFilter
              label={t('filter.orderingAccount')}
              selectedValues={sectionFilters}
              onValuesChange={setSectionFilters}
              availableOptions={availableSections}
              valueType="default"
            />
          )}

          {/* Price Alert Section Filters (Multi-select for Ordering Accounts) */}
          {availablePriceAlertSections.length > 0 && setPriceAlertSectionFilters && (
            <MultiSelectFilter
              label={t('filter.orderingAccount')}
              selectedValues={priceAlertSectionFilters}
              onValuesChange={setPriceAlertSectionFilters}
              availableOptions={availablePriceAlertSections}
              valueType="default"
            />
          )}

          {/* Platform Filters (Multi-select) */}
          {availablePlatforms.length > 0 && setPlatformFilters && (
            <MultiSelectFilter
              label="Suppliers"
              selectedValues={platformFilters}
              onValuesChange={setPlatformFilters}
              availableOptions={formatPlatformOptions(availablePlatforms)}
              valueType="default"
            />
          )}

          {/* Category Filters (Multi-select) */}
          {availableCategories.length > 0 && setCategoryFilters && (
            <MultiSelectFilter
              label="Categories"
              selectedValues={categoryFilters}
              onValuesChange={setCategoryFilters}
              availableOptions={availableCategories}
              valueType="category"
              maxHeight="max-h-[140px]"
            />
          )}

          {/* Subcategory Filters (Multi-select) */}
          {availableSubCategories && availableSubCategories.length > 0 && setSubCategoryFilters && (
            <MultiSelectFilter
              label="Subcategories"
              selectedValues={subCategoryFilters}
              onValuesChange={setSubCategoryFilters}
              availableOptions={availableSubCategories}
              valueType="subcategory"
              maxHeight="max-h-[140px]"
            />
          )}

          {/* Revenue Restaurant Filters (Multi-select, for "All Restaurants" view) */}
          {availableRevenueRestaurants.length > 0 && setRevenueRestaurantFilters && (
            <MultiSelectFilter
              label="Restaurants"
              selectedValues={revenueRestaurantFilters}
              onValuesChange={setRevenueRestaurantFilters}
              availableOptions={availableRevenueRestaurants}
              valueType="default"
              maxHeight="max-h-[90px]"
            />
          )}

          {/* Revenue Source Filters (Multi-select). Only shown when a restaurant has more
              than one venue feeding it — a single-source restaurant gets no useless filter. */}
          {availableRevenueSources.length > 1 && setRevenueSourceFilters && (
            <MultiSelectFilter
              label={t('filter.revenueSources')}
              selectedValues={revenueSourceFilters}
              onValuesChange={setRevenueSourceFilters}
              availableOptions={availableRevenueSources}
              valueType="default"
            />
          )}

          {/* Revenue Category Filters (Multi-select) */}
          {availableRevenueCategories.length > 0 && setRevenueCategoryFilters && (
            <MultiSelectFilter
              label={t('filter.revenueCategories')}
              selectedValues={revenueCategoryFilters}
              onValuesChange={setRevenueCategoryFilters}
              availableOptions={availableRevenueCategories}
              valueType="category"
            />
          )}

          {/* Menu Category Filters (Multi-select) */}
          {availableMenuCategories.length > 0 && setMenuCategoryFilters && (
            <MultiSelectFilter
              label={t('filter.menuCategories')}
              selectedValues={menuCategoryFilters}
              onValuesChange={setMenuCategoryFilters}
              availableOptions={availableMenuCategories}
              valueType="blueQuantity"
            />
          )}

          {/* Price Alert Restaurant Filters (Multi-select, for "All Restaurants" view) */}
          {availablePriceAlertRestaurants.length > 0 && setPriceAlertRestaurantFilters && (
            <MultiSelectFilter
              label="Restaurants"
              selectedValues={priceAlertRestaurantFilters}
              onValuesChange={setPriceAlertRestaurantFilters}
              availableOptions={availablePriceAlertRestaurants}
              valueType="default"
              maxHeight="max-h-[90px]"
            />
          )}

          {/* Price Alert Change Type Filter (TabbedSection: All/Increases/Decreases) */}
          {setPriceAlertChangeType && (
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                {t('priceAlerts.changes')}
              </label>
              <TabbedSections
                options={[
                  { value: 'all', label: t('overallType.all') },
                  { value: 'increases', label: t('priceAlerts.increases') },
                  { value: 'decreases', label: t('priceAlerts.decreases') },
                ]}
                value={priceAlertChangeType || 'all'}
                onChange={(value: string) => setPriceAlertChangeType(value as PriceAlertChangeType)}
                isTimeOptionSelection={false}
                maxTabsBeforeDropdown={3}
              />
            </div>
          )}

          {/* Price Alert Platform Filters (Multi-select) */}
          {availablePriceAlertPlatforms.length > 0 && setPriceAlertPlatformFilters && (
            <MultiSelectFilter
              label="Suppliers"
              selectedValues={priceAlertPlatformFilters}
              onValuesChange={setPriceAlertPlatformFilters}
              availableOptions={formatPlatformOptions(availablePriceAlertPlatforms)}
              valueType="default"
            />
          )}

          {/* Price Alert Category Filters (Multi-select) */}
          {availablePriceAlertCategories.length > 0 && setPriceAlertCategoryFilters && (
            <MultiSelectFilter
              label="Categories"
              selectedValues={priceAlertCategoryFilters}
              onValuesChange={setPriceAlertCategoryFilters}
              availableOptions={availablePriceAlertCategories}
              valueType="category"
            />
          )}
        </div>

        {/* Action buttons */}
        <div className="-mx-4 mt-4">
          <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
          <div className="flex justify-end gap-3 pt-4 px-4">
            <button
              onClick={onReset}
              className={cn(
                smallTextSize,
                secondaryText,
                semiBoldText,
                'rounded-xl',
                'px-4 py-3',
                whiteBG,
                'border',
                borderPrimary,
                'hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark',
                'flex-1'
              )}
            >
              {t('common.reset')}
            </button>
            <button
              onClick={onClose}
              className={cn(
                smallTextSize,
                'text-white dark:text-white',
                semiBoldText,
                'bg-darkGreenColor dark:bg-darkGreenColor-dark',
                'rounded-xl',
                'px-4 py-3',
                'hover:bg-darkGreenColor/95',
                'flex-1'
              )}
            >
              {t('common.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
